// src/components/PortalAluno/MeusDados.tsx
// UPDATED: 2026-03-27 — conectado ao backend real

import React, { useState, useEffect } from 'react';
import {
  User, Mail, Phone, MapPin, Calendar, Edit2,
  Save, X, CreditCard, Building2, Hash, Loader2, AlertTriangle, RefreshCw,
} from 'lucide-react';
import { useStudentProfile } from '../../hooks/useStudentProfile';
import apiClient from '../../services/api';

const inputBase = 'w-full px-4 py-2.5 border rounded-xl text-sm text-gray-700 transition-all focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-white hover:border-gray-300 border-gray-200';

const Field: React.FC<{
  label: string; icon: React.ReactNode; readOnly?: boolean; children: React.ReactNode;
}> = ({ label, icon, readOnly, children }) => (
  <div>
    <label className="flex items-center gap-1.5 text-[10.5px] font-semibold text-gray-400
      uppercase tracking-wider mb-1.5">
      <span className="text-gray-400">{icon}</span> {label}
      {readOnly && (
        <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-400
          border border-gray-200 font-semibold tracking-normal normal-case">
          somente leitura
        </span>
      )}
    </label>
    {children}
  </div>
);

const MeusDados: React.FC = () => {
  const { student, loading, error, refetch } = useStudentProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [saved, setSaved]         = useState(false);
  const [salvando, setSalvando]   = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [draft, setDraft] = useState({
    phone:     '',
    address:   '',
    city:      '',
    state:     '',
    whatsapp:  '',
  });

  // Preenche o draft quando carregar os dados
  useEffect(() => {
    if (student) {
      setDraft({
        phone:    student.phone   ?? '',
        address:  student.address ?? '',
        city:     student.city    ?? '',
        state:    student.state   ?? '',
        whatsapp: student.phone   ?? '',
      });
    }
  }, [student]);

  const upd = (key: keyof typeof draft) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setDraft(p => ({ ...p, [key]: e.target.value }));

  const handleSave = async () => {
    if (!student) return;
    setSalvando(true); setSaveError(null);
    try {
      await apiClient.patch(`/students/${student.id}`, {
        phone:   draft.phone   || null,
        address: draft.address || null,
        city:    draft.city    || null,
        state:   draft.state   || null,
      });
      setIsEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      refetch();
    } catch (e: any) {
      setSaveError(e?.message ?? 'Erro ao salvar. Tente novamente.');
    } finally { setSalvando(false); }
  };

  const handleCancel = () => {
    if (student) {
      setDraft({
        phone:    student.phone   ?? '',
        address:  student.address ?? '',
        city:     student.city    ?? '',
        state:    student.state   ?? '',
        whatsapp: student.phone   ?? '',
      });
    }
    setIsEditing(false); setSaveError(null);
  };

  const initials = student?.fullName?.split(' ').slice(0,2).map(n=>n[0]).join('').toUpperCase() ?? 'AL';

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="w-8 h-8 text-amber-500 animate-spin"/>
    </div>
  );

  if (error || !student) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
      <AlertTriangle className="w-10 h-10 text-amber-400"/>
      <p className="text-sm text-gray-600">{error ?? 'Perfil não encontrado'}</p>
      <button onClick={refetch}
        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
        <RefreshCw className="w-4 h-4"/> Tentar novamente
      </button>
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl">

      {/* ── HEADER ─────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="relative flex-shrink-0">
            <div className="absolute -inset-1 bg-gold-400/20 blur-[10px] rounded-full"/>
            <img src="/icons/icon11.png" alt="Meus Dados"
              className="relative w-11 h-11 object-contain drop-shadow-[0_2px_10px_rgba(212,175,55,0.45)]"
              onError={e => { (e.currentTarget as HTMLImageElement).style.display='none'; }}/>
          </div>
          <div>
            <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-none">Meus Dados</h1>
            <p className="text-xs text-gray-400 mt-1">Informações de cadastro e contato</p>
          </div>
        </div>

        {!isEditing ? (
          <button onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium
              text-[#1a1a1c] hover:shadow-lg transition-all self-start sm:self-auto"
            style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
            <Edit2 className="w-4 h-4"/> Editar
          </button>
        ) : (
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button onClick={handleCancel} disabled={salvando}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium
                text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50">
              <X className="w-4 h-4"/> Cancelar
            </button>
            <button onClick={handleSave} disabled={salvando}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium
                text-[#1a1a1c] transition-all disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
              {salvando ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
              Salvar
            </button>
          </div>
        )}
      </div>

      {/* ── TOASTS ─────────────────────────────────────────────────── */}
      {saved && (
        <div className="flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-emerald-50 border border-emerald-200">
          <Save className="w-4 h-4 text-emerald-600"/>
          <p className="text-sm font-medium text-emerald-700">Dados salvos com sucesso!</p>
        </div>
      )}
      {saveError && (
        <div className="flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-red-50 border border-red-200">
          <AlertTriangle className="w-4 h-4 text-red-500"/>
          <p className="text-sm font-medium text-red-700">{saveError}</p>
        </div>
      )}

      {/* ── PROFILE CARD ───────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100
        shadow-[0_1px_8px_rgba(0,0,0,0.05)] overflow-hidden">

        <div className="h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent"/>

        {/* Avatar + ID */}
        <div className="flex items-center gap-4 px-6 py-5 border-b border-gray-50">
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 rounded-full bg-amber-400/20 blur-[8px]"/>
            {student.photoUrl ? (
              <img src={student.photoUrl} alt="Foto"
                className="relative w-14 h-14 rounded-full object-cover ring-2 ring-amber-300/50"/>
            ) : (
              <div className="relative w-14 h-14 rounded-full flex items-center justify-center
                text-[#1a1a1c] font-semibold text-lg ring-2 ring-amber-300/50
                shadow-[0_0_16px_rgba(212,175,55,0.25)]"
                style={{ background: 'linear-gradient(135deg,#f4d03f,#b8860b)' }}>
                {initials}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold text-gray-900 truncate">{student.fullName}</p>
            <p className="text-xs text-gray-400 mt-0.5">{student.affiliate?.schoolName ?? 'Escola não informada'}</p>
          </div>
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-50 border border-gray-100 flex-shrink-0">
            <Hash className="w-3.5 h-3.5 text-gray-400"/>
            <span className="text-xs font-mono font-medium text-gray-600">{student.systemId}</span>
          </div>
        </div>

        {/* Campos */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">

          {/* Nome — somente leitura */}
          <div className="sm:col-span-2">
            <Field label="Nome Completo" icon={<User className="w-3.5 h-3.5"/>} readOnly>
              <p className="text-sm text-gray-500 py-2.5 px-4 bg-gray-50 rounded-xl border border-gray-100">{student.fullName}</p>
            </Field>
          </div>

          {/* E-mail — somente leitura */}
          <Field label="E-mail" icon={<Mail className="w-3.5 h-3.5"/>} readOnly>
            <p className="text-sm text-gray-500 py-2.5 px-4 bg-gray-50 rounded-xl border border-gray-100">
              {student.user?.email ?? '—'}
            </p>
          </Field>

          {/* Telefone — editável */}
          <Field label="Telefone" icon={<Phone className="w-3.5 h-3.5"/>}>
            {isEditing ? (
              <input type="tel" value={draft.phone} onChange={upd('phone')} className={inputBase} placeholder="(11) 99999-9999"/>
            ) : (
              <p className="text-sm text-gray-800 py-2.5 px-4 bg-gray-50 rounded-xl border border-gray-100">
                {student.phone ?? 'Não informado'}
              </p>
            )}
          </Field>

          {/* CPF — somente leitura */}
          <Field label="CPF / CIN" icon={<CreditCard className="w-3.5 h-3.5"/>} readOnly>
            <p className="text-sm text-gray-500 py-2.5 px-4 bg-gray-50 rounded-xl border border-gray-100 font-mono">
              {student.cpf ?? student.cin ?? 'Não informado'}
            </p>
          </Field>

          {/* Data Matrícula — somente leitura */}
          <Field label="Data de Matrícula" icon={<Calendar className="w-3.5 h-3.5"/>} readOnly>
            <p className="text-sm text-gray-500 py-2.5 px-4 bg-gray-50 rounded-xl border border-gray-100">
              {student.enrollmentDate ? new Date(student.enrollmentDate).toLocaleDateString('pt-BR') : '—'}
            </p>
          </Field>

          {/* Curso — somente leitura */}
          <Field label="Curso" icon={<Hash className="w-3.5 h-3.5"/>} readOnly>
            <p className="text-sm text-gray-500 py-2.5 px-4 bg-gray-50 rounded-xl border border-gray-100">
              {student.courseName ?? 'Não informado'}
            </p>
          </Field>

          {/* Escola — somente leitura */}
          <Field label="Escola" icon={<Building2 className="w-3.5 h-3.5"/>} readOnly>
            <p className="text-sm text-gray-500 py-2.5 px-4 bg-gray-50 rounded-xl border border-gray-100">
              {student.affiliate?.schoolName ?? 'Não informado'}
            </p>
          </Field>

          {/* Endereço — editável */}
          <div className="sm:col-span-2">
            <Field label="Endereço" icon={<MapPin className="w-3.5 h-3.5"/>}>
              {isEditing ? (
                <input type="text" value={draft.address} onChange={upd('address')} className={inputBase} placeholder="Rua, número, bairro"/>
              ) : (
                <p className="text-sm text-gray-800 py-2.5 px-4 bg-gray-50 rounded-xl border border-gray-100">
                  {student.address ?? 'Não informado'}
                  {student.city ? ` — ${student.city}` : ''}
                  {student.state ? `/${student.state}` : ''}
                </p>
              )}
            </Field>
          </div>

          {isEditing && (
            <>
              <Field label="Cidade" icon={<MapPin className="w-3.5 h-3.5"/>}>
                <input type="text" value={draft.city} onChange={upd('city')} className={inputBase} placeholder="Cidade"/>
              </Field>
              <Field label="Estado (UF)" icon={<MapPin className="w-3.5 h-3.5"/>}>
                <input type="text" maxLength={2} value={draft.state} onChange={upd('state')} className={inputBase} placeholder="SP"/>
              </Field>
            </>
          )}
        </div>

        <div className="px-6 pb-5">
          <p className="text-[11px] text-gray-400 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0"/>
            Nome, e-mail, CPF e escola só podem ser alterados pela Federação.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MeusDados;
